import { useRouter } from "next/router";
import React, { ReactElement } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { passengerCodesIntl } from "shared/lib/constants";
import { isEmptyObject } from "shared/lib/functions";

import { TravelPoliciesBadges } from "@components/Flights/TravelPolicies";
import PriceFormatter from "@components/Formatters/PriceFormatter";
import FormattedMessage from "@components/UI/FormattedMessage";

type Props = {
  passengerPrices: any[];
  travelPoliciesResults?: any[];
  backgroundImageUrl?: string | boolean;
};

export default function ResultRecapitulation({
  passengerPrices,
  travelPoliciesResults = [],
  backgroundImageUrl = false,
}: Props) {
  const intl = useIntl();
  const router = useRouter();
  const {
    alternativeCurrencyFinalPrice,
    alternativeCurrencyPaymentPrice,
    alternativeCurrencyOperatorFee,
    alternativeCurrencyServicesPrices,
    finalPrice,
    additionalServicesPrice,
    paymentPrice,
    currency,
    operatorFee,
    servicesPrices,
    allowedTravelerTypes,
    serviceFee,
    additionalServices,
  } = useSelector((state: AppState) => ({
    alternativeCurrencyFinalPrice: state.price.alternativeCurrencyFinalPrice,
    alternativeCurrencyAdditionalServicesPrice:
      state.price.alternativeCurrencyAdditionalServicesPrice,
    alternativeCurrencyAdditionalServices:
      state.price.alternativeCurrencyAdditionalServices,
    alternativeCurrencyServicesPrice:
      state.price.alternativeCurrencyServicesPrice,
    alternativeCurrencyPaymentPrice:
      state.price.alternativeCurrencyPaymentPrice,
    alternativeCurrencyOperatorFee: state.price.alternativeCurrencyOperatorFee,
    alternativeCurrencyServicesPrices:
      state.price.alternativeCurrencyServicesPrices,
    finalPrice: state.price.finalPrice,
    additionalServicesPrice: state.price.sumAdditionalServicesPrice,
    additionalServices: state.price.additionalServices,
    servicesPrice: state.price.servicesPrice,
    paymentPrice: state.price.paymentPrice,
    currency: state.price.currency || state.search.currency,
    operatorFee: state.price.operatorFee,
    servicesPrices: state.price.servicesPrices,
    allowedTravelerTypes: state.requestorConfig.allowedTravelerTypes,
    serviceFee:
      state.storage?.frontendSettings?.dealerFrontendSettings?.priceTable
        ?.serviceFee,
  }));

  return (
    <div className={backgroundImageUrl ? "result-form-box-wrapper" : ""}>
      <h3 className="h3 result-h3">
        <FormattedMessage id="Result.summary" />
      </h3>
      <div className="result-summary-box">
        {passengerPrices.map((passenger, index) => {
          let passengerTitle = "";

          if (allowedTravelerTypes?.length > 0) {
            passengerTitle =
              allowedTravelerTypes?.find(
                (travelerType) => travelerType.Code === passenger.code
              )?.$t ||
              (passengerCodesIntl[passenger.code]
                ? intl.formatMessage({
                    id: passengerCodesIntl[passenger.code],
                  })
                : passenger.code);
          }

          return (
            <div key={index} className="result-summary-box-item">
              <span
                className="result-summary-box-labels"
                id={`recapitulation-passenger-type-${index}`}
              >
                {[
                  passengerTitle,
                  passenger.count > 1 ? ` (x${passenger.count})` : "",
                ]}
              </span>
              <div
                className="result-summary-box-price"
                id={`recapitulation-passenger-price-${index}`}
              >
                <b>
                  <PriceFormatter
                    price={
                      serviceFee !== "true"
                        ? passenger.price
                        : `${
                            Number(passenger.price) -
                            Number(passenger.operatorFee)
                          }`
                    }
                    customCurrency={router.query.currency || currency}
                  />
                </b>
              </div>
            </div>
          );
        })}

        {(!!servicesPrices && !isEmptyObject(servicesPrices)
          ? servicesPrices
          : []
        ).map((servicePrice, index) => {
          if (!Number(servicePrice.Price) || Number(servicePrice.Price) === 0) {
            return null;
          }

          return (
            <PriceLine
              description={servicePrice.Label.$t}
              price={Number(
                router.query.currency
                  ? alternativeCurrencyServicesPrices[index].Price
                  : servicePrice.Price
              )}
              currency={router.query.currency || currency}
              id={`${servicePrice.Service}-fee`}
              key={`${servicePrice.Service}-fee-${Number(servicePrice.Price)}`}
            />
          );
        })}

        {serviceFee === "true" && (
          <PriceLine
            description={
              <FormattedMessage id="ResultRecapitulation.serviceFee" />
            }
            price={Number(
              router.query.currency
                ? alternativeCurrencyOperatorFee
                : operatorFee
            )}
            currency={router.query.currency || currency}
            id="serivce-fees"
          />
        )}

        {additionalServices?.servicePrices?.external &&
          Object.values(
            additionalServices.servicePrices.external
          ).map(
            (
              service: { price: number; marketplaceServiceName: string },
              index: number
            ) => (
              <PriceLine
                key={index}
                description={service?.marketplaceServiceName}
                price={Number(service?.price)}
                currency={router.query.currency || currency}
                id="additional-services"
              />
            )
          )}

        <PriceLine
          description={
            <FormattedMessage id="ResultRecapitulation.additionalServices" />
          }
          price={additionalServicesPrice}
          currency={router.query.currency || currency}
          id="additional-services"
        />

        {Number(paymentPrice) > 0 && (
          <PriceLine
            description={
              <FormattedMessage id="ResultRecapitulation.paymentFees" />
            }
            price={
              router.query.currency
                ? alternativeCurrencyPaymentPrice
                : paymentPrice
            }
            currency={router.query.currency || currency}
            id="payment-fees"
          />
        )}

        <div className="result-summary-box-divider" />
        <div className="result-summary-prices">
          <span className="result-summary-prices-h">
            <FormattedMessage id="Result.priceTogether" />
          </span>
          <br />
          <b>
            <PriceFormatter
              price={
                Number(
                  router.query.currency
                    ? alternativeCurrencyFinalPrice
                    : finalPrice
                ) +
                Number(
                  router.query.currency
                    ? alternativeCurrencyPaymentPrice
                    : paymentPrice
                )
              }
              customCurrency={router.query.currency || currency}
              customClass="result-summary-prices-price"
              id="recapitulation-summary-price"
            />
          </b>
          <TravelPoliciesBadges travellers={travelPoliciesResults} size={18} />
          <div className="result-summary-prices-dph">
            <FormattedMessage id="Result.vatIncluded" />
          </div>
        </div>
      </div>
    </div>
  );
}

const PriceLine = ({
  description,
  price,
  currency,
  id,
}: {
  description: string | ReactElement;
  price: number;
  currency: string;
  id: string;
}) => {
  if (price === 0 || price === undefined) return null;

  return (
    <div className="result-summary-box-item">
      <span
        className="result-summary-box-labels"
        id={`recapitulation-additional-line-type-${id}`}
      >
        <strong style={{ marginRight: "5px" }}>+</strong>
        {description}
      </span>
      <div className="result-summary-box-price">
        <b>
          <PriceFormatter
            price={price}
            customCurrency={currency}
            id={`recapitulation-additional-line-price-${id}`}
          />
        </b>
      </div>
    </div>
  );
};
